import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, MoreVertical, ExternalLink, Link2, Trash2, ArrowUpDown } from 'lucide-react';
import {
  APT_STATUS_COLORS,
  formatAptRent,
  formatAptRooms,
  formatAptSize,
  pmLabel,
  sourceLabel,
  statusLabel,
} from '@/lib/apartmentFormat';

const SORT_KEYS = ['title', 'status', 'rent', 'rooms', 'size', 'applications', 'eligible', 'visits', 'manager', 'source'];

function sortValue(apt, key) {
  switch (key) {
    case 'title': return (apt.title || '').toLowerCase();
    case 'status': return apt.status || '';
    case 'rent': return Number(apt.rent) || 0;
    case 'rooms': return Number(apt.rooms) || 0;
    case 'size': return Number(apt.size) || 0;
    case 'applications': return apt.crm_active_proposals ?? 0;
    case 'visits': return apt.crm_visit_count ?? 0;
    case 'manager': return pmLabel(apt.property_manager).toLowerCase();
    case 'eligible': return apt.crm_eligible_count ?? 0;
    case 'source': return apt.source || '';
    default: return '';
  }
}

export default function ApartmentsDatabase({
  apartments,
  propertyManagers,
  onDelete,
  addHref = '/admin/apartments/new',
  selectedIds = [],
  onSelectionChange,
  onMatchListings,
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');

  const sources = useMemo(() => {
    const set = new Set();
    apartments.forEach((a) => { if (a.source) set.add(a.source); });
    return Array.from(set).sort();
  }, [apartments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = apartments.filter((apt) => {
      if (statusFilter !== 'all' && apt.status !== statusFilter) return false;
      if (pmFilter !== 'all') {
        if (pmFilter === 'none' && apt.property_manager_id) return false;
        if (pmFilter !== 'none' && apt.property_manager_id !== pmFilter) return false;
      }
      if (sourceFilter !== 'all' && apt.source !== sourceFilter) return false;
      if (!q) return true;
      const hay = [apt.title, apt.address, apt.neighborhood, pmLabel(apt.property_manager), apt.source]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    rows = [...rows].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [apartments, search, statusFilter, pmFilter, sourceFilter, sortKey, sortDir]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const availableFiltered = useMemo(
    () => filtered.filter((a) => a.status === 'available'),
    [filtered],
  );
  const allAvailableSelected = availableFiltered.length > 0
    && availableFiltered.every((a) => selectedSet.has(a.id));

  const toggleRow = (aptId, checked) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (checked) next.add(aptId);
    else next.delete(aptId);
    onSelectionChange(Array.from(next));
  };

  const toggleSelectAll = (checked) => {
    if (!onSelectionChange) return;
    if (!checked) {
      onSelectionChange([]);
      return;
    }
    onSelectionChange(availableFiltered.map((a) => a.id));
  };


  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortHead = ({ label, col }) => (
    <th className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-[#0B3D2E]"
        onClick={() => toggleSort(col)}
      >
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === col ? 'opacity-100' : 'opacity-30'}`} />
      </button>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="apartments-database">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-lg text-[#2B2B2B]">{t('admin.apartments')}</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {onMatchListings && selectedIds.length > 0 && (
            <Button
              className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white text-sm"
              onClick={onMatchListings}
              data-testid="match-listings-btn"
            >
              <Link2 className="w-4 h-4 mr-1" /> {t('admin.bulkMatch.matchListings')} ({selectedIds.length})
            </Button>
          )}
          <Button
            className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white text-sm"
            onClick={() => navigate(addHref)}
            data-testid="add-apartment-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {t('admin.addApartment')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:flex-wrap bg-white border border-[#2B2B2B]/10 rounded-lg p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5a5a5a]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.apt.searchPlaceholder')}
            className="pl-8 h-9 text-sm"
            data-testid="apt-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-full lg:w-[130px] text-xs" data-testid="apt-status-filter">
            <SelectValue placeholder={t('admin.apt.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t('admin.apt.filterAllStatuses')}</SelectItem>
            <SelectItem value="available" className="text-xs">{t('admin.apt.statusAvailable')}</SelectItem>
            <SelectItem value="matched" className="text-xs">{t('admin.apt.statusRented')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="h-9 w-full lg:w-[150px] text-xs" data-testid="apt-pm-filter">
            <SelectValue placeholder={t('admin.apt.filterManager')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t('admin.apt.filterAllManagers')}</SelectItem>
            <SelectItem value="none" className="text-xs">{t('admin.apt.filterNoManager')}</SelectItem>
            {propertyManagers.filter((pm) => !pm.archived).map((pm) => (
              <SelectItem key={pm.id} value={pm.id} className="text-xs">
                {pm.first_name} {pm.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="h-9 w-full lg:w-[130px] text-xs" data-testid="apt-source-filter">
            <SelectValue placeholder={t('admin.apt.filterSource')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t('admin.apt.filterAllSources')}</SelectItem>
            {sources.map((s) => (
              <SelectItem key={s} value={s} className="text-xs capitalize">{sourceLabel(s, t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-[#5a5a5a] lg:ml-auto tabular-nums">
          {filtered.length} / {apartments.length}
        </p>
      </div>

      <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]" data-testid="apartments-crm-table">
            <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
              <tr>
                <th className="w-10 px-2 py-2.5">
                  <Checkbox
                    checked={allAvailableSelected}
                    onCheckedChange={(v) => toggleSelectAll(!!v)}
                    disabled={availableFiltered.length === 0}
                    aria-label={t('admin.bulkMatch.selectAll')}
                    data-testid="apt-select-all"
                  />
                </th>
                <SortHead label={t('admin.apt.colApartment')} col="title" />
                <SortHead label={t('admin.apt.colEligible')} col="eligible" />
                <SortHead label={t('admin.apt.colStatus')} col="status" />
                <SortHead label={t('admin.apt.colRent')} col="rent" />
                <SortHead label={t('admin.apt.colRooms')} col="rooms" />
                <SortHead label={t('admin.apt.colSize')} col="size" />
                <SortHead label={t('admin.apt.colApplications')} col="applications" />
                <SortHead label={t('admin.apt.colVisits')} col="visits" />
                <SortHead label={t('admin.apt.colManager')} col="manager" />
                <SortHead label={t('admin.apt.colSource')} col="source" />
                <th className="text-right px-3 py-2.5 font-semibold w-12">{t('admin.apt.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2B2B2B]/5">
              {filtered.map((apt) => (
                <tr
                  key={apt.id}
                  className={`hover:bg-[#F5F3EF]/70 cursor-pointer ${apt.crm_needs_attention ? 'bg-amber-50/30' : ''}`}
                  onClick={() => navigate(`/admin/apartments/${apt.id}`)}
                  data-testid={`apt-row-${apt.id}`}
                >
                  <td className="px-2 py-2 w-10" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedSet.has(apt.id)}
                      disabled={apt.status !== 'available'}
                      onCheckedChange={(v) => toggleRow(apt.id, !!v)}
                      data-testid={`apt-select-${apt.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-[#2B2B2B] max-w-[200px] truncate">{apt.title || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${APT_STATUS_COLORS[apt.status] || 'bg-gray-100 text-gray-700'} border-0 text-[10px]`}>
                      {statusLabel(apt.status, t)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatAptRent(apt)}</td>
                  <td className="px-3 py-2 tabular-nums">{formatAptRooms(apt)}</td>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatAptSize(apt)}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{apt.crm_eligible_count ?? 0}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{apt.crm_active_proposals ?? 0}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{apt.crm_visit_count ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-[#5a5a5a] max-w-[100px] truncate">{pmLabel(apt.property_manager)}</td>
                  <td className="px-3 py-2 text-xs text-[#5a5a5a] capitalize">{sourceLabel(apt.source, t)}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`apt-menu-${apt.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="text-xs">
                        <DropdownMenuItem onClick={() => navigate(`/admin/apartments/${apt.id}`)}>
                          {t('admin.apt.viewProfile')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/apartments/${apt.id}/edit`)}>
                          {t('admin.apt.edit')}
                        </DropdownMenuItem>
                        {apt.external_url && (
                          <DropdownMenuItem asChild>
                            <a href={apt.external_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3 h-3 mr-2" /> {t('admin.apt.viewListing')}
                            </a>
                          </DropdownMenuItem>
                        )}
                        {apt.status === 'available' && (
                          <DropdownMenuItem onClick={() => navigate(`/admin/apartments/${apt.id}?match=1`)}>
                            <Link2 className="w-3 h-3 mr-2" /> {t('admin.match')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => onDelete(apt.id)}>
                          <Trash2 className="w-3 h-3 mr-2" /> {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-[#5a5a5a] text-sm text-center py-12">{t('admin.apt.empty')}</p>
        )}
      </div>
    </div>
  );
}
